"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import Script from "next/script";
import {
  ANALYTICS_NO_TRACK_PATHS,
  ANALYTICS_EVENTS,
} from "@/lib/analytics-events";
import { identify, track } from "@/lib/analytics";

interface Props {
  /**
   * GA4 Measurement ID (`G-XXXXXXXXXX`). Comes from
   * `NEXT_PUBLIC_GA4_MEASUREMENT_ID` at build time. Sin el ID, el
   * provider es no-op (no inyecta el script, no manda eventos) y la
   * app sigue funcionando — útil para local dev sin GA configurado.
   */
  measurementId?: string;
  /**
   * Usuario autenticado, si lo hay. Viene del SSR cookie read en el
   * layout — al pasarlo aquí podemos llamar identify() de inmediato
   * en lugar de esperar un fetch client-side, lo que reduce los
   * "anonymous events" antes del stitching.
   */
  user?: {
    id: string;
    email?: string;
  } | null;
  children: React.ReactNode;
}

/**
 * Google Analytics 4 wrapper para la web del cliente.
 *
 * Comportamiento:
 *   1. Carga `gtag.js` con `next/script strategy="afterInteractive"`
 *      cuando hay measurementId. Sin ID, no se inyecta nada — la
 *      app degrada limpia.
 *   2. Inicializa con `send_page_view: false` para que NOSOTROS
 *      controlemos cuándo y qué pagina se reporta — el App Router
 *      no dispara navigation events que GA4 detecte automáticamente,
 *      así que sin esto se pierden las navegaciones SPA.
 *   3. Re-emite `page_view` en cada cambio de pathname, excepto en
 *      rutas listadas en {@link ANALYTICS_NO_TRACK_PATHS} (checkout
 *      con tokens de Stripe en la URL).
 *   4. Si hay usuario logueado, hace identify() — GA4 stitchea la
 *      sesión actual con sesiones previas del mismo `user_id` (si
 *      Google Signals está activado).
 *
 * IMPORTANTE: GA4 no captura nada por sí solo más allá de page_view +
 * Enhanced Measurement (scroll, outbound, video). Para e-commerce,
 * suscripciones, login etc. usar `track()` desde call sites con los
 * eventos definidos en `lib/analytics-events.ts`.
 */
export default function AnalyticsProvider({
  measurementId,
  user,
  children,
}: Props) {
  const pathname = usePathname();

  // ── Page view en cada navegación SPA ────────────────────────────
  useEffect(() => {
    if (!measurementId || typeof window === "undefined") return;
    if (ANALYTICS_NO_TRACK_PATHS.some((p) => pathname?.startsWith(p))) return;
    track("page_view" as never, {
      page_path: pathname,
      page_location: window.location.href,
    });
  }, [pathname, measurementId]);

  // ── Identify al montar si hay user ──────────────────────────────
  useEffect(() => {
    if (!measurementId || !user?.id) return;
    identify(user.id);
  }, [measurementId, user?.id]);

  // Referencia muda para que el linter no marque ANALYTICS_EVENTS
  // como import sin usar — el catálogo se importa para que cualquier
  // dev nuevo lo encuentre cuando lee este provider.
  void ANALYTICS_EVENTS;

  if (!measurementId) return <>{children}</>;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
        strategy="afterInteractive"
      />
      <Script id="ga4-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', '${measurementId}', {
            send_page_view: false,
            cookie_flags: 'SameSite=Lax;Secure',
          });
        `}
      </Script>
      {children}
    </>
  );
}
